import { useQuery, type QueryStatus } from "@tanstack/react-query";

import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { message, formatSnakeCase } from "@/lib/utils";
import { getScoreBarColor } from "@/lib/chart";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

interface ScoreItem {
  label: string;
  score: number;
}

function ScoreCard({ label, score }: ScoreItem) {
  return (
    <Card>
      <CardContent className="px-5 space-y-2">
        <p className="text-3xl font-bold">{score}</p>
        <CardDescription>{label}</CardDescription>
        <Progress value={score} className="h-1.5 bg-secondary" indicatorClassName={getScoreBarColor(score)} />
      </CardContent>
    </Card>
  );
}

function ScoreCardsGrid({ scores }: { scores: ScoreItem[] }) {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
      {scores.map((item) => (
        <ScoreCard key={item.label} {...item} />
      ))}
    </div>
  );
}

function ScoreCardsContent({ status, scores }: { status: QueryStatus; scores: ScoreItem[] }) {
  switch (status) {
    case "pending":
      return <ScoreCardsSkeleton />;
    case "error":
      return <LockedCard message={message} className="mb-10" />;
    case "success":
      return scores.length ? <ScoreCardsGrid scores={scores} /> : <LockedCard message={message} className="mb-10" />;
  }
}

export function AuditScoreCards() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchCreativeScores());

  const scores = Object.entries(data?.median_scores ?? {})
    .map(([key, raw]) => ({
      label: formatSnakeCase(key),
      score: Math.round(raw * 100),
    }))
    .filter((s) => s.score > 0);

  return <ScoreCardsContent status={status} scores={scores} />;
}

function ScoreCardsSkeleton() {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 3 }, (_, i) => (
        <Card key={i}>
          <CardContent className="px-5 space-y-2">
            <Skeleton className="h-9 w-16" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-1.5 w-full rounded-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
